import { useTranslations } from "use-intl";
import dayjs from "dayjs";
import { Alert } from "@mui/material";
import type { FindUserPackageBookingByIdPackageBooking } from "@/app/lib/types/codegen";
import { ImageCarousel } from "../ImageCarousel";
import { DetailsList, DetailsListItem } from "../DetailsList";
import { RoomList } from "../RoomList";

type Props = {
  trip: FindUserPackageBookingByIdPackageBooking;
};

export function OverviewCard({ trip }: Props) {
  const t = useTranslations();

  return (
    <div className="rounded-3xl bg-white p-1">
      <ImageCarousel
        imagesUrls={trip.package.imageUrls || []}
        imageRatio="3/2"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.package.name}
        </div>
        <div className="text-sm text-text-tertiary">
          {trip.package.subtitle}
        </div>

        <Alert
          severity="info"
          sx={{
            my: "calc(var(--spacing)*3)",
          }}
        >
          <div className="mb-1 text-sm font-medium text-black">
            {t("trips.bookingInfo.title")}
          </div>
          <div className="text-sm text-text-placeholder">
            {t("trips.bookingInfo.content")}
          </div>
        </Alert>

        <DetailsList>
          <DetailsListItem
            title={t("trips.bookedOn")}
            value={dayjs(trip.bookedAt).format("D MMMM, YYYY")}
          />

          {/* to add spacing */}
          <DetailsListItem title="" value="" />

          <DetailsListItem
            title={t("trips.travelStartDate")}
            value={dayjs(trip.startDate).format("D MMMM, YYYY")}
          />

          <DetailsListItem
            title={t("trips.travelEndDate")}
            value={dayjs(trip.startDate)
              .add(trip.package.durationInDays, "day")
              .format("D MMMM, YYYY")}
          />

          <DetailsListItem
            title={t("trips.travelLength")}
            value={t("trips.duration", {
              nights: trip.package.durationInDays - 1,
              days: trip.package.durationInDays,
            })}
          />

          <DetailsListItem
            title={t("trips.flightOperator")}
            value={trip.package.airline}
          />

          <DetailsListItem
            title={t("trips.cities")}
            value={trip.package.cities}
          />

          <DetailsListItem
            title={t("trips.countries")}
            value={trip.package.countries.map((c) => c.name).join(", ")}
          />
        </DetailsList>

        <div className="mt-3 mb-1 text-sm text-text-disabled-primary">
          {t("trips.tripDetails.rooms")}
        </div>
        <RoomList
          rooms={{
            adultInDoubleRoom: trip.numberOfAdultsDoubleRoom,
            adultInSingleRoom: trip.numberOfAdultsSingleRoom,
            childWithBed: trip.numberOfChildrenWithBed,
            childWithoutBed: trip.numberOfChildrenWithoutBed,
            infant: trip.numberOfInfants,
          }}
        />

        <div className="mt-5 text-sm">{trip.package.description}</div>
      </div>
    </div>
  );
}
